import { useQueryClient } from '@tanstack/react-query'
import { Button, Col, DatePicker, Flex, Form, Modal, Row, Select } from 'antd'
import { ModalProps } from 'antd/lib'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePersonRiskGroupServiceAddPersonRiskGroupEndpoint,
  usePersonRiskGroupServiceGetPersonRiskGroupClassifierEndpoint,
  usePersonRiskGroupServiceGetPersonRiskGroupsEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { message } from 'src/components'
import { dayMonthYearFormatWithDot, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  onCancel: () => void
} & ModalProps

export const AddToRiskGroupModal = ({ onCancel, ...props }: Props) => {
  const { t, currentLang } = useLocales()
  const { id } = useParams()

  const queryClient = useQueryClient()

  const [form] = Form.useForm()
  const startDate = Form.useWatch('startDate', form)

  const { data } =
    usePersonRiskGroupServiceGetPersonRiskGroupClassifierEndpoint({
      personId: String(id),
      startDate: dayjs(startDate).format(yearMonthDaySlash)
    })

  const { mutate } = usePersonRiskGroupServiceAddPersonRiskGroupEndpoint({
    onError: (error: ApiError) => formErrorHandler(error.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonRiskGroupServiceGetPersonRiskGroupsEndpointKey]
      })
      message.success(t('risk_groups.success_add'))
      onCancel()
    }
  })

  const onSave = async () => {
    const values = await form.validateFields()

    mutate({
      requestBody: {
        ...values,
        startDate: dayjs(values?.startDate).format(yearMonthDaySlash),
        endDate: values?.endDate
          ? dayjs(values?.endDate).format(yearMonthDaySlash)
          : undefined
      },
      personId: String(id)
    })
  }

  useEffect(() => {
    form.setFieldValue('startDate', dayjs())
  }, [])

  return (
    <Modal
      open
      maskClosable={false}
      title={t('risk_groups.add_to_group')}
      footer={null}
      onCancel={onCancel}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Form.Item
              name="startDate"
              rules={requiredRules}
              label={t('risk_groups.start_date')}
            >
              <DatePicker
                className="full-width"
                format={dayMonthYearFormatWithDot}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="endDate" label={t('risk_groups.end_date')}>
              <DatePicker
                className="full-width"
                format={dayMonthYearFormatWithDot}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item
              name="riskGroupId"
              rules={requiredRules}
              label={t('risk_groups.risk_group')}
            >
              <Select
                placeholder={t('risk_groups.risk_group')}
                options={data?.data?.map((item) => ({
                  value: item.id,
                  label: item?.translation?.[currentLang?.value]
                }))}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Flex justify="center" gap={16}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <Button type="primary" onClick={onSave}>
                {t('common.save')}
              </Button>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
